// storeApi — Mağaza (2. el) API istemcisi + veri adaptörü.
//
// Website'taki include/process/store/api.php (adaptör) ve api/store.php (kullanıcı
// işlemleri) ile birebir. Panel kullanıcısı zaten girişli: çağrılar panel token'ıyla
// DOĞRUDAN API'ye gider (POST {API_URL}flow/<metod>, JSON, Authorization: Bearer).
// Zarf: {status, result | message}. Her çağrı {ok, sonuc, mesaj} döner, hiç fırlatmaz.
//
// Çekirdek uçlar BE'nin classes/store.php'sinde, ekler (favori, fiyat alarmı, nakliye/
// harita noktası, konuşmalar) classes/store-ext.php'de.
//
// Adaptör (urunCevir) API ilanını görünümlerin kullandığı ürün nesnesine çevirir; alan
// adları website storeUrunCevir ile aynıdır (ad, fiyat, durum, saticiTipi, gorseller, kapak…).
import axios from "axios";
import { API_URL } from "../../services";
import { COKLU_ADET_MAX, cokluAdet } from "./storeData";

/** Mağaza sitesinin kökü (görseller, "Sitede gör" / paylaş linkleri). API adresinden ortam seçilir:
 *  stage.troydepoapi.com → https://stage.troydepo.com, prod.troydepoapi.com → https://troydepo.com
 *  (prod'da "prod." öneki sitede YOK — eski replace("troydepoapi.", "troydepo.") prod'da prod.troydepo.com üretiyordu). */
export const SITE_URL = /\/\/stage[.-]/i.test(String(API_URL || "")) ? "https://stage.troydepo.com" : "https://troydepo.com";

/* ======================================================================= oturum */

function oku(k) {
  try {
    return localStorage.getItem(k) || sessionStorage.getItem(k) || "";
  } catch (e) {
    return "";
  }
}

export const storeToken = () => oku("token");
export const storeUserID = () => oku("userID");

/* ======================================================================= çağrı */

/** API mesajını kullanıcıya uygun Türkçeye çevirir (website stMesaj). */
export function apiMesaj(m, varsayilan = "İşlem yapılamadı. Lütfen tekrar deneyin.") {
  const s = String(m || "").trim();
  if (s === "" || /^(You do not have permission|unknown_error|Action must be required|not_permissioned)/i.test(s)) return varsayilan;
  if (/invalid token|token.*(expired|invalid)/i.test(s)) return "Oturumunuzun süresi doldu. Lütfen yeniden giriş yapın.";
  const eksik = s.match(/^Missing parameters?\s*\[(.*)\]/i);
  if (eksik) return `Eksik bilgi: ${eksik[1].trim()}`;
  if (/not.?found/i.test(s)) return varsayilan;
  return s;
}

/**
 * POST flow/<metod>. Dönüş: {ok, sonuc, mesaj} — sonuc API'nin result'ı (yoksa {}),
 * mesaj hata metni (ham). Ağ hatasında da fırlatmaz.
 */
export async function storeCall(metod, govde = {}) {
  const token = storeToken();
  if (!token)
    return {
      ok: false,
      sonuc: null,
      mesaj: "Oturum bulunamadı. Lütfen yeniden giriş yapın.",
    };
  try {
    const r = await axios({
      method: "post",
      url: `${API_URL}flow/${metod}`,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      data: govde || {},
    });
    const d = r?.data || {};
    if (d.status) return { ok: true, sonuc: d.result ?? {}, mesaj: "" };
    return { ok: false, sonuc: null, mesaj: String(d.message || "") };
  } catch (e) {
    const m = e?.response?.data?.message;
    return {
      ok: false,
      sonuc: null,
      mesaj: m ? String(m) : "Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.",
    };
  }
}

/* ======================================================================= sabitler */

/** Ürün durumları (API conditions; get-store-category-attributes ile de gelir). */
export const DURUMLAR = {
  sifir: "Sıfır",
  "yeni-gibi": "Yeni gibi",
  iyi: "İyi",
  yipranmis: "Yıpranmış",
};

/** İlan durum kodları (store_listings.status). */
export const ILAN_DURUM = {
  0: "Ödeme bekliyor",
  1: "Onay bekliyor",
  2: "Yayında",
  3: "Reddedildi",
  4: "Süresi doldu",
  5: "Satıldı",
};

/* ======================================================================= kategoriler */

/** API ana kategori kodu → çizgi ikon adı (StoreIcon). */
function ikonKod(kod) {
  const m = {
    "01": "sofa",
    "02": "fridge",
    "03": "tv",
    "04": "office",
    "05": "warehouse",
    "07": "tree",
    "08": "dumbbell",
    10: "lamp",
  };
  return m[String(kod || "")] || "box";
}

/** get-store-categories → [{slug, ad, ikon, id, sayi, alt:[{slug, ad, id, sayi, not}]}] (slug = slug_key). */
export function kategorilerCevir(r, tipler = {}) {
  return (Array.isArray(r?.categories) ? r.categories : []).map((k) => ({
    slug: String(k.slug_key || ""),
    tamSlug: String(k.slug || k.slug_key || ""),
    ad: String(k.name || ""),
    ikon: ikonKod(k.code),
    id: String(k.category_id || ""),
    sayi: Number(k.listingCount) || 0,
    alt: (Array.isArray(k.children) ? k.children : []).map((a) => ({
      slug: String(a.slug_key || ""),
      tamSlug: String(a.slug || a.slug_key || ""),
      ad: String(a.name || ""),
      id: String(a.category_id || ""),
      sayi: Number(a.listingCount) || 0,
      not: String(a.scope_note || ""),
      // 3. katman (ürün tipi; store-ext get-store-product-types) — "Diğer" en sonda.
      tipler: (Array.isArray(tipler?.[a.slug_key]) ? tipler[a.slug_key] : []).map((t) => ({
        slug: String(t.slugKey || ""),
        ad: String(t.name || ""),
        id: String(t.categoryID || ""),
        sayi: Number(t.count) || 0,
      })),
    })),
  }));
}

// Katalog herkese aynı: oturum boyunca 5 dk önbellekli tek istek.
let katSoz = null;
let katZaman = 0;
export function kategorilerGetir(tazele = false) {
  if (!tazele && katSoz && Date.now() - katZaman < 300000) return katSoz;
  katZaman = Date.now();
  katSoz = Promise.all([storeCall("get-store-categories", { withCount: true }), storeCall("get-store-product-types", {})]).then(
    ([r, t]) => {
      if (!r.ok) {
        katSoz = null;
        return {
          ok: false,
          liste: [],
          mesaj: apiMesaj(r.mesaj, "Kategoriler alınamadı."),
        };
      }
      return { ok: true, liste: kategorilerCevir(r.sonuc, t.ok ? t.sonuc?.types || {} : {}), mesaj: "" };
    },
  );
  return katSoz;
}

/** Ana / alt / 3. katman (ürün tipi) slug'ını çözer: {tip:'ana'|'alt'|'urunTipi', kategori, alt, urunTipi}. */
export function kategoriBul(kategoriler, slug) {
  const s = String(slug || "");
  for (const k of kategoriler || []) {
    if (k.slug === s || k.tamSlug === s) return { tip: "ana", kategori: k, alt: null, urunTipi: null };
    for (const a of k.alt) {
      if (a.slug === s || a.tamSlug === s || `${k.slug}/${a.slug}` === s) return { tip: "alt", kategori: k, alt: a, urunTipi: null };
      const t = (a.tipler || []).find((x) => x.slug === s);
      if (t) return { tip: "urunTipi", kategori: k, alt: a, urunTipi: t };
    }
  }
  return null;
}

/** API kategori adı/slug'ından [anaSlug, altSlug]. */
function anaAlt(kategoriler, slugVeyaAd) {
  const s = String(slugVeyaAd || "");
  if (!s) return ["", ""];
  for (const k of kategoriler || []) {
    if (k.slug === s || k.tamSlug === s || k.ad === s) return [k.slug, ""];
    for (const a of k.alt) {
      if (a.slug === s || a.tamSlug === s || a.ad === s || `${k.slug}/${a.slug}` === s) return [k.slug, a.slug];
    }
  }
  return ["", ""];
}

// Kapsam kuralı (store-ext get-store-scope-rule): eşikler + muaf alt kategoriler — 10 dk önbellek.
let kapsamOnbellek = null;
function kapsamKurali() {
  if (kapsamOnbellek && Date.now() - kapsamOnbellek.t < 600000) return kapsamOnbellek.soz;
  const soz = storeCall("get-store-scope-rule", {}).then((r) =>
    r.ok
      ? {
          minKenar: Number(r.sonuc?.minEdgeCm) || 60,
          minKg: Number(r.sonuc?.minWeightKg) || 15,
          muaf: Array.isArray(r.sonuc?.exempt) ? r.sonuc.exempt : [],
          binaYok: Array.isArray(r.sonuc?.pickupSimple) ? r.sonuc.pickupSimple : Array.isArray(r.sonuc?.exempt) ? r.sonuc.exempt : [],
        }
      : { minKenar: 60, minKg: 15, muaf: [], binaYok: [] },
  );
  kapsamOnbellek = { t: Date.now(), soz };
  return soz;
}

// Kategori alanları (ölçüler dahil) — kategori başına 10 dk önbellek.
const ozellikOnbellek = {};
export function kategoriOzellikleri(kategori) {
  const k = String(kategori || "");
  const c = ozellikOnbellek[k];
  if (c && Date.now() - c.t < 600000) return c.soz;
  const soz = Promise.all([storeCall("get-store-category-attributes", { category: k }), kapsamKurali()]).then(([r, kural]) => {
    if (!r.ok) {
      delete ozellikOnbellek[k];
      return {
        ok: false,
        mesaj: apiMesaj(r.mesaj, "Kategori alanları yüklenemedi."),
      };
    }
    const attrs = (Array.isArray(r.sonuc?.attributes) ? r.sonuc.attributes : [])
      .slice()
      .sort((x, y) => Number(x.sort_order) - Number(y.sort_order));
    const slugKey = k.split("/").pop();
    return {
      ok: true,
      attributes: attrs,
      conditions: r.sonuc?.conditions || DURUMLAR,
      category: r.sonuc?.category || null,
      kapsam: {
        minKenar: kural.minKenar,
        minKg: kural.minKg,
        muaf: kural.muaf.includes(slugKey),
        // Bina bilgisi sorulmayan alt kategoriler: ayrı ayar (store_settings.pickup_simple_categories).
        binaYok: kural.binaYok.includes(slugKey),
      },
    };
  });
  ozellikOnbellek[k] = { t: Date.now(), soz };
  return soz;
}

/* ======================================================================= adaptör */

const OLCU_KEYS = ["en_cm", "boy_cm", "yukseklik_cm"];

/**
 * API ilanı (search satırı, get-listing, favori/alarm/ilanlarım satırı) → ürün nesnesi.
 * Website storeUrunCevir ile aynı alanlar.
 */
export function urunCevir(l, kategoriler = []) {
  const x = l || {};
  const detay = x.listingID !== undefined;
  const id = String(x.listingID ?? x.listing_id ?? "");
  const loc = x.location || {};
  const troy = (loc.type ?? x.location_type ?? "") === "troy_depot";
  const katSlug = detay ? String(x.category?.slug || "") : String(x.category_slug || "");
  const [ana, alt] = anaAlt(kategoriler, katSlug !== "" ? katSlug : String(x.category_name || ""));

  const nitelikler = [];
  const olcu = { en: null, boy: null, yuk: null };
  let demonte = "";
  (Array.isArray(x.attributes) ? x.attributes : []).forEach((a) => {
    if (!a || typeof a !== "object") return;
    const k = String(a.key || "");
    if (k === "en_cm") olcu.en = Number(a.value) || null;
    if (k === "boy_cm") olcu.boy = Number(a.value) || null;
    if (k === "yukseklik_cm") olcu.yuk = Number(a.value) || null;
    if (k === "demonte") demonte = String(a.value ?? "");
    nitelikler.push({
      key: k,
      label: String(a.label || k),
      deger: a.value ?? "",
      gosterim: String(a.display ?? a.value ?? ""),
    });
  });
  const olculer =
    olcu.en && olcu.boy ? `${Math.trunc(olcu.en)} x ${Math.trunc(olcu.boy)}${olcu.yuk ? ` x ${Math.trunc(olcu.yuk)}` : ""} cm` : "";
  const hacim = olcu.en && olcu.boy && olcu.yuk ? Math.round((olcu.en * olcu.boy * olcu.yuk) / 100000) / 10 : 0;

  const gorseller = detay
    ? (Array.isArray(x.images) ? x.images : []).map((s) => String(s || "")).filter(Boolean)
    : [String(x.cover_image || "")].filter(Boolean);
  const konumAd = detay
    ? [loc.district, loc.city]
        .map((s) => String(s || "").trim())
        .filter(Boolean)
        .join(", ")
    : // Liste satırı: ilçe/il önce (API locationLabel Troy ürününde depo adı / "Troy Depo" der).
      [x.district, x.city]
        .map((s) => String(s || "").trim())
        .filter(Boolean)
        .join(", ") || String(x.locationLabel || "");
  const durumAnahtar = String(x.condition ?? x.condition_key ?? "");
  const seller = x.seller || {};
  const sayi = (v) => (v === null || v === undefined || v === "" ? 0 : Number(v) || 0);

  const u = {
    listingID: id,
    slug: String(x.slug || ""),
    ad: String(x.title || ""),
    kategori: ana,
    altKategori: alt,
    kategoriAd: String(x.category?.name ?? x.category_name ?? ""),
    fiyat: Number(x.price) || 0,
    eskiFiyat: 0,
    marka: String(x.brand || ""),
    // 19.10.2026: toplu ilanda ürün durumu sorulmaz (sabit "iyi" gider); rozet "Toplu satış" (website storeUrunCevir ile aynı)
    durum: ana === "toplu-satis" ? "Toplu satış" : String(x.conditionLabel || DURUMLAR[durumAnahtar] || ""),
    durumAnahtar,
    olculer,
    hacim,
    depo: String(loc.depotName ?? x.depot_name ?? ""),
    saticiTipi: troy ? "troy" : "uye",
    // Troy Depo ürünü depoda değilse ("Diğer" — depo adı boş) konum "İlçe, İl" olarak görünür.
    konum: konumAd,
    teslimYeri: troy && String(loc.depotName ?? x.depot_name ?? "").trim() ? "Depodan" : String(loc.delivery || ""),
    teslimNot: String(loc.note || ""),
    satici: {
      ad: String(seller.displayName || ""),
      adGorunum: "tam",
      iletisim: String(seller.contactPref || "mesaj"),
      telefon: "",
      // API maskesi ulusal biçimde ("5321 *** ** 67"); açılan numarayla aynı görünsün: "0532 *** ** 67".
      telMaskeli: String(seller.phoneMasked || "").replace(/^(5\d{2})\d /, "0$1 "),
      revealable: !!seller.canReveal,
      sahibi: !!seller.isOwner,
    },
    // Satış izni (store_listings.open_to_sale): özet satırlarında gelir; detayda ilanGetir ekler'den doldurur.
    // Yalnız satışa açık ilan sepete eklenir; müşteriye ayrıca gösterilmez.
    satista: String(x.open_to_sale ?? "0") !== "0" && !!x.open_to_sale,
    demonte,
    stok: cokluAdet(alt) ? COKLU_ADET_MAX : 1,
    rozet: "",
    aciklama: String(x.description || ""),
    ozellikler: [],
    nitelikler,
    gorseller,
    kapak: gorseller[0] || "",
    durumKod: x.status === undefined || x.status === null || x.status === "" ? 2 : Number(x.status),
    durumAd: String(x.state || ""),
    redSebebi: String(x.rejectReason ?? x.reject_reason ?? ""),
    pazarlik: !!x.isNegotiable,
    goruntulenme: sayi(x.viewCount ?? x.view_count),
    yayin: sayi(x.publishedDateTime ?? x.published_date_time),
    bitis: sayi(x.expiresDateTime ?? x.expires_date_time),
    sehir: String(loc.city ?? x.city ?? ""),
    ilce: String(loc.district ?? x.district ?? ""),
    _detay: detay,
  };
  nitelikler.forEach((n) => {
    if (OLCU_KEYS.includes(n.key)) return;
    if (n.gosterim === "" || (n.gosterim === "0" && n.key !== "agirlik_kg")) return;
    u.ozellikler.push(`${n.label}: ${n.gosterim === "1" ? "Evet" : n.gosterim}`);
  });
  return u;
}

/* ======================================================================= katalog */

/** search-listings → {ok, urunler, toplam, mesaj}. */
export async function ilanAra(filtre = {}) {
  const [{ liste: kats }, r] = await Promise.all([
    kategorilerGetir(),
    storeCall("search-listings", { limit: 60, sort: "newest", ...filtre }),
  ]);
  if (!r.ok)
    return {
      ok: false,
      urunler: [],
      toplam: 0,
      mesaj: apiMesaj(r.mesaj, "İlanlar alınamadı."),
    };
  const urunler = (Array.isArray(r.sonuc?.listings) ? r.sonuc.listings : []).map((x) => urunCevir(x, kats));
  await eskiFiyatDoldur(urunler);
  return {
    ok: true,
    urunler,
    toplam: Number(r.sonuc?.total) || urunler.length,
    mesaj: "",
  };
}

/** Kart listeleri: indirimden önceki fiyatlar tek istekte (store-ext get-listing-original-prices; yalnız fiyattan
 *  büyük olanlar gelir). eskiFiyat > fiyat ise kart üstü çizili fiyat + indirim rozeti gösterir. Website ile aynı. */
async function eskiFiyatDoldur(urunler) {
  const ids = urunler.map((u) => u.listingID).filter(Boolean);
  if (!ids.length) return;
  const r = await storeCall("get-listing-original-prices", { listingIDs: ids });
  const o = (r.ok && r.sonuc?.originalPrices) || {};
  urunler.forEach((u) => {
    const v = Number(o[u.listingID]);
    if (v > Number(u.fiyat)) u.eskiFiyat = Math.round(v * 100) / 100;
  });
}

/** get-listing (slug) — sahibi kendi yayında olmayan ilanını da görür. {ok, urun, mesaj}. */
export async function ilanGetir(slug) {
  const s = String(slug || "");
  if (!/^[a-z0-9-]{1,200}$/.test(s)) return { ok: false, urun: null, mesaj: "İlan bulunamadı." };
  const [{ liste: kats }, r] = await Promise.all([kategorilerGetir(), storeCall("get-listing", { slug: s })]);
  if (!r.ok || !r.sonuc?.listingID)
    return {
      ok: false,
      urun: null,
      mesaj: apiMesaj(r.mesaj, "İlan bulunamadı."),
    };
  const urun = urunCevir(r.sonuc, kats);
  const ek = await storeCall("get-listing-extras", {
    listingID: r.sonuc.listingID,
  });
  urun.satista = !!(ek.ok && ek.sonuc?.openToSale);
  urun.video = ek.ok ? String(ek.sonuc?.videoUrl || "") : "";
  // İlana özel kargo ücreti (panelden admin; null = hacim tarifesi) — storeData.kargoUcreti önce bunu kullanır
  if (ek.ok && ek.sonuc?.shippingFee !== null && ek.sonuc?.shippingFee !== undefined) urun.kargoUcreti = Number(ek.sonuc.shippingFee);
  // İndirimden önceki (üstü çizili) fiyat
  if (ek.ok && Number(ek.sonuc?.originalPrice) > Number(urun.fiyat)) urun.eskiFiyat = Number(ek.sonuc.originalPrice);
  return { ok: true, urun, mesaj: "" };
}

/** get-listing-extras → {shippingPayer, lat?, lng?, videoUrl, pickupInfo, productType, openToSale, nameDisplay, shippingFee}
 *  (hata/eksikte boş). lat/lng ve nameDisplay yalnız ilan sahibine ve admine gelir. */
export async function ilanEkleri(listingID) {
  if (!listingID) return { shippingPayer: "", videoUrl: "" };
  const r = await storeCall("get-listing-extras", { listingID });
  return r.ok
    ? {
        shippingPayer: String(r.sonuc?.shippingPayer || ""),
        lat: r.sonuc?.lat ?? null,
        lng: r.sonuc?.lng ?? null,
        videoUrl: String(r.sonuc?.videoUrl || ""),
        pickupInfo: r.sonuc?.pickupInfo && typeof r.sonuc.pickupInfo === "object" ? r.sonuc.pickupInfo : null,
        productType: r.sonuc?.productType && typeof r.sonuc.productType === "object" ? r.sonuc.productType : null,
        openToSale: !!r.sonuc?.openToSale,
        nameDisplay: String(r.sonuc?.nameDisplay || ""),
        shippingFee: r.sonuc?.shippingFee ?? null,
        originalPrice: r.sonuc?.originalPrice ?? null,
        // 26.10.2026: isteğe bağlı sıfır ürün fiyatı (TL) ve alınma yılı; null = girilmedi
        newPrice: r.sonuc?.newPrice ?? null,
        purchaseYear: r.sonuc?.purchaseYear ?? null,
      }
    : { shippingPayer: "", videoUrl: "" };
}

/** Fiyat geçmişi: eskiden yeniye [{t, fiyat, olay}] (website storePriceHistory). */
export function gecmisLog(gecmis, u) {
  const h = (Array.isArray(gecmis) ? gecmis : []).slice().reverse(); // API yeniden eskiye
  const log = [];
  let onceki = null;
  h.forEach((x) => {
    const f = Number(x.price) || 0;
    if (onceki !== null && Math.abs(f - onceki) < 0.005) return;
    const olay = onceki === null ? "İlan yayınlandı" : f < onceki ? "Fiyat düştü" : "Fiyat arttı";
    log.push({ t: Number(x.t) || 0, fiyat: Math.round(f * 100) / 100, olay }); // kuruş korunur (29,99)
    onceki = f;
  });
  if (!log.length && u)
    log.push({
      t: u.yayin || Math.floor(Date.now() / 1000),
      fiyat: Math.round(u.fiyat * 100) / 100,
      olay: "İlan yayınlandı",
    });
  return log;
}

export async function fiyatGecmisi(u) {
  if (!u?.listingID || ![2, 5].includes(u.durumKod)) return gecmisLog([], u);
  const r = await storeCall("get-listing-price-history", {
    listingID: u.listingID,
    limit: 50,
  });
  return gecmisLog(r.ok ? r.sonuc?.history : [], u);
}

/* ======================================================================= alıcı işlemleri */

export async function favoriler() {
  const [{ liste: kats }, r] = await Promise.all([kategorilerGetir(), storeCall("get-listing-favorites", {})]);
  if (!r.ok)
    return {
      ok: false,
      urunler: [],
      idler: [],
      mesaj: apiMesaj(r.mesaj, "Favoriler alınamadı."),
    };
  const urunler = (Array.isArray(r.sonuc?.listings) ? r.sonuc.listings : []).map((x) => urunCevir(x, kats));
  const idler = (Array.isArray(r.sonuc?.listingIDs) ? r.sonuc.listingIDs : urunler.map((u) => u.listingID)).map(String);
  return { ok: true, urunler, idler, mesaj: "" };
}

export async function favoriAyarla(listingID, ekle) {
  const r = await storeCall(ekle ? "add-listing-favorite" : "remove-listing-favorite", { listingID });
  return r.ok ? { ok: true, favori: !!r.sonuc?.isFavorite } : { ok: false, mesaj: apiMesaj(r.mesaj) };
}

/** Alarm satırları: {urun, listingID, slug, hedef, baz, kurulma(unix), ulasti, log}. */
export async function fiyatAlarmlari() {
  const [{ liste: kats }, r] = await Promise.all([kategorilerGetir(), storeCall("get-price-alarms", {})]);
  if (!r.ok)
    return {
      ok: false,
      alarmlar: [],
      mesaj: apiMesaj(r.mesaj, "Fiyat alarmları alınamadı."),
    };
  const alarmlar = (Array.isArray(r.sonuc?.alarms) ? r.sonuc.alarms : []).map((x) => {
    const u = urunCevir(x, kats);
    return {
      urun: u,
      listingID: u.listingID,
      slug: u.slug,
      hedef: x.targetPrice === null || x.targetPrice === undefined ? null : Number(x.targetPrice),
      baz: x.basePrice === null || x.basePrice === undefined ? null : Number(x.basePrice),
      kurulma: Number(x.alarmCreated) || 0,
      ulasti: !!x.reached,
      log: gecmisLog(x.history, u),
    };
  });
  return { ok: true, alarmlar, mesaj: "" };
}

export async function alarmKur(listingID, hedef) {
  const r = await storeCall("set-price-alarm", {
    listingID,
    targetPrice: hedef === "" || hedef === undefined ? null : hedef,
  });
  return r.ok ? { ok: true, sonuc: r.sonuc } : { ok: false, mesaj: apiMesaj(r.mesaj, "Alarm kurulamadı.") };
}

export async function alarmSil(listingID) {
  const r = await storeCall("remove-price-alarm", { listingID });
  return r.ok ? { ok: true } : { ok: false, mesaj: apiMesaj(r.mesaj, "Alarm kaldırılamadı.") };
}

/** Satıcının numarası (günlük sınırlı). {ok, telefon (biçimli), tel (rakam), kalan}. */
export async function telefonGoster(listingID) {
  const r = await storeCall("reveal-listing-phone", { listingID });
  if (!r.ok) return { ok: false, mesaj: apiMesaj(r.mesaj, "Numara gösterilemedi."), telOnay: telOnayMi(r.mesaj) };
  let t = String(r.sonuc?.phone || "").replace(/\D/g, "");
  if (t.length === 12 && t.startsWith("90")) t = t.slice(2);
  if (t.length === 10) t = `0${t}`;
  const telefon = t.length === 11 ? `${t.slice(0, 4)} ${t.slice(4, 7)} ${t.slice(7, 9)} ${t.slice(9)}` : t;
  return { ok: true, telefon, tel: t, kalan: Number(r.sonuc?.remaining) || 0 };
}

/** İlk mesaj (ilan) ya da var olan konuşmaya cevap. {ok, conversationID}. */
export async function mesajGonder({ listingID, conversationID, metin }) {
  const govde = String(metin || "").trim();
  if (govde === "") return { ok: false, mesaj: "Mesaj boş olamaz." };
  const r = conversationID
    ? await storeCall("send-conversation-message", {
        conversationID,
        body: govde,
      })
    : await storeCall("send-listing-message", { listingID, body: govde });
  if (!r.ok) return { ok: false, mesaj: apiMesaj(r.mesaj, "Mesaj gönderilemedi.") };
  return {
    ok: true,
    conversationID: String(r.sonuc?.conversationID || conversationID || ""),
  };
}

const AYLAR_KISA = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const ayniGun = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const saatYaz = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

/** Unix sn → "14:05" (bugün) · "Dün" · "3 Eki" (website storeZamanKisa). */
export function zamanKisa(t) {
  if (!t) return "";
  const d = new Date(t * 1000);
  const simdi = new Date();
  if (ayniGun(d, simdi)) return saatYaz(d);
  const dun = new Date(simdi.getTime() - 86400000);
  if (ayniGun(d, dun)) return "Dün";
  return `${d.getDate()} ${AYLAR_KISA[d.getMonth()]}`;
}

/** Mesaj balonu zamanı: bugünse saat, değilse "3 Eki 14:05". */
export function mesajZamani(t) {
  if (!t) return "";
  const d = new Date(t * 1000);
  return ayniGun(d, new Date()) ? saatYaz(d) : `${zamanKisa(t)} ${saatYaz(d)}`;
}

/** Konuşmalar (store-ext): [{id, listingID, slug, kisi, urun, fiyat, kapak, troy, rol, okunmamis, son, t, zaman}]. */
export async function konusmalar() {
  const r = await storeCall("get-store-conversations", {});
  if (!r.ok)
    return {
      ok: false,
      liste: [],
      mesaj: apiMesaj(r.mesaj, "Mesajlar alınamadı."),
    };
  const liste = (Array.isArray(r.sonuc?.conversations) ? r.sonuc.conversations : []).map((c) => {
    const t = Number(c.lastMessageDateTime) || 0;
    return {
      id: String(c.conversationID || ""),
      listingID: String(c.listingID || ""),
      slug: String(c.slug || ""),
      kisi: String(c.counterpart || "Üye"),
      urun: String(c.title || ""),
      fiyat: Number(c.price) || 0,
      kapak: String(c.coverImage || ""),
      troy: !!c.isTroy,
      rol: String(c.role || ""),
      okunmamis: Number(c.unread) || 0,
      son: String(c.lastBody || ""),
      t,
      zaman: zamanKisa(t),
    };
  });
  return { ok: true, liste, mesaj: "" };
}

/** Konuşmanın mesajları (açan tarafın okunmamış sayacı API'de sıfırlanır). */
export async function konusmaMesajlari(conversationID) {
  const r = await storeCall("get-listing-messages", { conversationID });
  if (!r.ok)
    return {
      ok: false,
      mesajlar: [],
      mesaj: apiMesaj(r.mesaj, "Mesajlar alınamadı."),
    };
  const ben = String(storeUserID());
  const mesajlar = (Array.isArray(r.sonuc?.messages) ? r.sonuc.messages : []).map((m) => ({
    id: String(m.message_id || ""),
    ben: String(m.sender_id) === ben,
    metin: String(m.body || ""),
    saat: mesajZamani(Number(m.created_date_time) || 0),
  }));
  return { ok: true, mesajlar, mesaj: "" };
}

export async function okunmamisSayisi() {
  const r = await storeCall("get-store-unread-count", {});
  return r.ok ? Number(r.sonuc?.unread) || 0 : 0;
}

/* ======================================================================= satıcı işlemleri */

/** Üyenin ilanları: [{urun, durum, durumKod, redSebebi}] (kaldırılanlar hariç). */
export async function ilanlarim() {
  const [{ liste: kats }, r] = await Promise.all([kategorilerGetir(), storeCall("get-my-listings", { limit: 100 })]);
  if (!r.ok)
    return {
      ok: false,
      ilanlar: [],
      mesaj: apiMesaj(r.mesaj, "İlanlarınız alınamadı."),
    };
  const ilanlar = (Array.isArray(r.sonuc?.listings) ? r.sonuc.listings : [])
    .filter((x) => Number(x.status) !== -1)
    .map((x) => {
      const u = urunCevir(x, kats);
      return {
        urun: u,
        durum: ILAN_DURUM[u.durumKod] || "Yayında",
        durumKod: u.durumKod,
        redSebebi: u.redSebebi,
      };
    });
  return { ok: true, ilanlar, mesaj: "" };
}

export async function ilanUcretBilgisi() {
  const r = await storeCall("get-listing-fee-info", {});
  return r.ok ? { ok: true, ...r.sonuc } : { ok: false, mesaj: apiMesaj(r.mesaj) };
}

/**
 * İlanı kaydeder (create-listing / update-listing) + ekler (set-listing-extras).
 * ilan: {listingID?, category, title, description, brand, condition, price, isNegotiable,
 *        city, district, addressNote, deliveryType, contactPref, nameDisplay, attributes,
 *        images[url | {name, extension, file}], shippingPayer, lat, lng}
 * Dönüş: {ok, listingID, slug, durum, odemeGerekli, ucret, yenidenOnay, mesaj}.
 */
export async function ilanKaydet(ilan) {
  const v = ilan || {};
  const govde = {
    title: String(v.title || ""),
    description: String(v.description || ""),
    brand: String(v.brand || ""),
    condition: String(v.condition || ""),
    price: Number(v.price) || 0,
    isNegotiable: !!v.isNegotiable,
    city: String(v.city || ""),
    district: String(v.district || ""),
    addressNote: String(v.addressNote || ""),
    deliveryType: String(v.deliveryType || ""),
    contactPref: ["telefon", "ikisi"].includes(v.contactPref) ? v.contactPref : "mesaj",
    nameDisplay: String(v.nameDisplay || "kisa"),
    attributes: v.attributes && typeof v.attributes === "object" ? v.attributes : {},
    images: (Array.isArray(v.images) ? v.images : []).slice(0, 30),
  };
  if (v.category) govde.category = String(v.category);
  let r;
  if (v.listingID) {
    r = await storeCall("update-listing", {
      ...govde,
      listingID: String(v.listingID),
    });
  } else {
    r = await storeCall("create-listing", { ...govde, locationType: "seller" });
  }
  if (!r.ok) return { ok: false, mesaj: apiMesaj(r.mesaj, "İlan kaydedilemedi."), telOnay: telOnayMi(r.mesaj) };
  const x = r.sonuc || {};
  const lid = String(x.listingID || v.listingID || "");
  // Ekler: nakliye kime ait + harita noktası (store-ext). Kaydı bozmaz; yalnız sonucu bildirilir.
  const ek = await storeCall("set-listing-extras", {
    listingID: lid,
    shippingPayer: String(v.shippingPayer || ""),
    lat: v.lat === null || v.lat === undefined ? "" : v.lat,
    lng: v.lng === null || v.lng === undefined ? "" : v.lng,
    pickupInfo: v.pickupInfo && typeof v.pickupInfo === "object" ? v.pickupInfo : {},
    productType: String(v.productType || ""),
    // İndirim: anahtar verildiyse yazılır ("" = indirim yok). Fiyat güncellemesinden SONRA (API fiyattan büyük olmasını ister).
    ...("originalPrice" in v ? { originalPrice: v.originalPrice === "" || v.originalPrice === null ? "" : Number(v.originalPrice) } : {}),
    // 26.10.2026: sıfır ürün fiyatı / alınma yılı — anahtar verildiyse yazılır ("" = temizle)
    ...("newPrice" in v ? { newPrice: v.newPrice === "" || v.newPrice === null ? "" : Number(v.newPrice) } : {}),
    ...("purchaseYear" in v ? { purchaseYear: v.purchaseYear === "" || v.purchaseYear === null ? "" : String(v.purchaseYear) } : {}),
  });
  kategorilerGetir(true); // ilan sayıları değişti
  return {
    ok: true,
    listingID: lid,
    slug: String(x.slug || v.slug || ""),
    durum: x.status === undefined ? 1 : Number(x.status),
    odemeGerekli: x.nextStep === "odeme-gerekli",
    // 19.10.2026 ilan paketleri: ilan hakkı yok → taslak; FE paket penceresini açar (PaketPenceresi)
    paketGerekli: x.nextStep === "paket-gerekli",
    ucret: Number(x.fee) || 0,
    yenidenOnay: !!x.reSubmitted,
    ekKaydedildi: ek.ok,
  };
}

/** İlan videosu (store-ext, en fazla 1, ≤ 30 MB). video: {ext, file(base64)} → yükler; null → kaldırır. */
export const VIDEO_MAX_MB = 30;
export async function ilanVideoKaydet(listingID, video) {
  const govde = video
    ? {
        listingID,
        video: {
          name: `ilan-video.${video.ext}`,
          extension: video.ext,
          file: video.file,
        },
      }
    : { listingID, remove: true };
  const r = await storeCall("set-listing-video", govde);
  return r.ok
    ? {
        ok: true,
        videoUrl: String(r.sonuc?.videoUrl || ""),
        yenidenOnay: !!r.sonuc?.reSubmitted, // video değişince yayındaki ilan yeniden onaya düşer
        pickupInfo: r.sonuc?.pickupInfo && typeof r.sonuc.pickupInfo === "object" ? r.sonuc.pickupInfo : null,
        productType: r.sonuc?.productType && typeof r.sonuc.productType === "object" ? r.sonuc.productType : null,
      }
    : { ok: false, mesaj: apiMesaj(r.mesaj, "Video kaydedilemedi.") };
}

export async function ilanKaldir(listingID) {
  const r = await storeCall("remove-listing", { listingID });
  return r.ok ? { ok: true } : { ok: false, mesaj: apiMesaj(r.mesaj, "İlan kaldırılamadı.") };
}

export async function ilanSatildi(listingID) {
  const r = await storeCall("mark-listing-sold", { listingID });
  return r.ok ? { ok: true } : { ok: false, mesaj: apiMesaj(r.mesaj, "İlan güncellenemedi.") };
}

/** İlan ücreti ödemesi (PayTR iframe). {ok, iframeUrl, tutar}. */
export async function odemeBaslat(listingID) {
  const r = await storeCall("init-listing-payment", { listingID });
  if (!r.ok) return { ok: false, mesaj: apiMesaj(r.mesaj, "Ödeme başlatılamadı.") };
  const url = String(r.sonuc?.iframeUrl || "");
  if (!/^https:\/\/www\.paytr\.com\//.test(url)) return { ok: false, mesaj: "Ödeme sayfası alınamadı." };
  return { ok: true, iframeUrl: url, tutar: Number(r.sonuc?.amount) || 0 };
}

/* ---- İlan süresi uzatma (store-ext; yalnız stage). Website api/store.php ilan-uzat / ilan-uzat-kontrol ile aynı. ---- */

/** Uzatma ücreti + gün: {ok, ucret, gun}. */
export async function uzatmaBilgi() {
  const r = await storeCall("get-listing-renewal-info", {});
  return r.ok ? { ok: true, ucret: Number(r.sonuc?.fee) || 0, gun: Number(r.sonuc?.days) || 30 } : { ok: false, mesaj: apiMesaj(r.mesaj) };
}

/** Uzatma ödemesini başlatır (PayTR iframe): {ok, uzatmaID, iframeUrl, tutar, gun}. */
export async function uzatmaBaslat(listingID) {
  const r = await storeCall("init-listing-renewal", { listingID });
  if (!r.ok) return { ok: false, mesaj: apiMesaj(r.mesaj, "Uzatma ödemesi başlatılamadı.") };
  const url = String(r.sonuc?.iframeUrl || "");
  if (!/^https:\/\/www\.paytr\.com\//.test(url)) return { ok: false, mesaj: "Ödeme sayfası alınamadı." };
  return {
    ok: true,
    uzatmaID: String(r.sonuc.renewalID),
    iframeUrl: url,
    tutar: Number(r.sonuc.amount) || 0,
    gun: Number(r.sonuc.days) || 0,
  };
}

/** Uzatma ödeme durumu (PayTR'ye de sorar): {ok, durum, bitis}. */
export async function uzatmaKontrol(uzatmaID) {
  const r = await storeCall("check-listing-renewal", { renewalID: uzatmaID });
  return r.ok
    ? { ok: true, durum: String(r.sonuc?.status || ""), bitis: Number(r.sonuc?.expiresDateTime) || 0 }
    : { ok: false, mesaj: apiMesaj(r.mesaj) };
}

/* ---- İlan paketleri / ilan hakkı (19.10.2026; store-ext + store.php). Website api/store.php paket-* / ilan-hak-* ile aynı. ---- */

/** Paketler + üyenin hakları: {ok, paketler:[{size, price, unitPrice}], maxAdet, bakiye, ucretsizKalan, gun, gecmis, siparisler}. */
export async function paketBilgi() {
  const [p, k] = await Promise.all([storeCall("get-listing-packages", {}), storeCall("get-my-listing-credits", {})]);
  if (!p.ok || !k.ok) return { ok: false, mesaj: apiMesaj((!p.ok ? p : k).mesaj, "Paket bilgisi alınamadı.") };
  return {
    ok: true,
    paketler: Array.isArray(p.sonuc?.packages) ? p.sonuc.packages : [],
    maxAdet: Number(p.sonuc?.maxQty) || 20,
    bakiye: Number(k.sonuc?.balance) || 0,
    ucretsizKalan: Number(k.sonuc?.freeLeft) || 0,
    gun: Number(k.sonuc?.activeDays) || 30,
    gecmis: Array.isArray(k.sonuc?.history) ? k.sonuc.history : [],
    siparisler: Array.isArray(k.sonuc?.orders) ? k.sonuc.orders : [],
  };
}

/** 26.10.2026 — ilan hakkı indirim kodu önizlemesi (kullanım saymaz; tutar API'de paketlerden hesaplanır):
    items [{size, qty}], kod → {ok, gecerli, kod, mesaj, tutar, indirim, odenecek}. */
export async function paketIndirim(items, kod) {
  const r = await storeCall("preview-credit-purchase", { items, promoCode: String(kod || "").trim().toUpperCase() });
  if (!r.ok) return { ok: false, mesaj: apiMesaj(r.mesaj, "Kod şu an doğrulanamadı. Lütfen tekrar deneyin.") };
  const p = r.sonuc?.promo || {};
  return {
    ok: true,
    gecerli: !!p.valid,
    kod: String(p.code || kod || ""),
    mesaj: String(p.message || ""),
    tutar: Number(r.sonuc?.amount) || 0,
    indirim: Number(r.sonuc?.discount) || 0,
    odenecek: Number(r.sonuc?.payable) || 0,
  };
}

/** Paket satın alma (PayTR iframe): items [{size, qty}], kod? (ilan hakkı indirim kodu) → {ok, siparisID, iframeUrl, hak, tutar, indirim}.
    Kod tutarın tamamını karşılarsa ödeme olmaz: {ok, odendi: true, siparisID, hak, bakiye}. */
export async function paketSatinAl(items, kod = "") {
  const k = String(kod || "").trim().toUpperCase();
  const r = await storeCall("init-credit-purchase", k ? { items, promoCode: k } : { items });
  if (!r.ok) return { ok: false, mesaj: apiMesaj(r.mesaj, "Paket ödemesi başlatılamadı.") };
  if (r.sonuc?.paid) {
    return { ok: true, odendi: true, siparisID: String(r.sonuc.orderID), hak: Number(r.sonuc.credits) || 0, bakiye: Number(r.sonuc.balance) || 0 };
  }
  const url = String(r.sonuc?.iframeUrl || "");
  if (!/^https:\/\/www\.paytr\.com\//.test(url)) return { ok: false, mesaj: "Ödeme sayfası alınamadı." };
  return {
    ok: true,
    siparisID: String(r.sonuc.orderID),
    iframeUrl: url,
    hak: Number(r.sonuc.credits) || 0,
    tutar: Number(r.sonuc.amount) || 0,
    indirim: Number(r.sonuc.discount) || 0,
  };
}

/** Paket ödeme durumu (PayTR'ye de sorar): {ok, durum, bakiye}. */
export async function paketDurum(siparisID) {
  const r = await storeCall("check-credit-purchase", { orderID: siparisID });
  return r.ok
    ? { ok: true, durum: String(r.sonuc?.status || ""), bakiye: Number(r.sonuc?.balance) || 0 }
    : { ok: false, mesaj: apiMesaj(r.mesaj) };
}

/** 1 ilan hakkı kullanır: tur "ilan" (taslağı onaya gönder) | "yenile" (süresi doluyu yayına al). {ok, durum, bakiye, bitis}. */
export async function ilanHakKullan(listingID, tur) {
  const r = await storeCall(tur === "yenile" ? "renew-listing-with-credit" : "use-listing-credit", { listingID });
  return r.ok
    ? {
        ok: true,
        durum: Number(r.sonuc?.status) || 0,
        bakiye: Number(r.sonuc?.creditBalance) || 0,
        bitis: Number(r.sonuc?.expiresDateTime) || 0,
      }
    : { ok: false, mesaj: apiMesaj(r.mesaj, "İlan hakkı kullanılamadı.") };
}

/* ---- Üyelik doğrulama (19.10.2026; website index.php stTelOnay ile aynı). Telefonu SMS koduyla onaylanmamış üye ilan
   veremez ve satıcı numarasını göremez (API store.php createListing / revealListingPhone reddeder; admin muaf). ---- */
export const telOnayMi = (mesaj) => /Telefon numaranızı onaylamanız/i.test(String(mesaj || ""));

/** {ok, onayli, telefon} — store-verification-status */
export async function telDurum() {
  const r = await storeCall("store-verification-status");
  return r.ok
    ? { ok: true, onayli: !!r.sonuc?.phoneVerified, telefon: String(r.sonuc?.phone || "") }
    : { ok: false, mesaj: apiMesaj(r.mesaj) };
}

/** Hesaptaki numaraya 6 haneli SMS kodu: {ok, onayli (zaten onaylı), telefon, tekrar (sn)} */
export async function telKodGonder() {
  const r = await storeCall("store-send-phone-otp");
  return r.ok
    ? {
        ok: true,
        onayli: !!r.sonuc?.alreadyVerified,
        telefon: String(r.sonuc?.phone || ""),
        tekrar: Number(r.sonuc?.resendIn) || 60,
      }
    : { ok: false, mesaj: apiMesaj(r.mesaj, "Kod gönderilemedi.") };
}

/** SMS kodunu doğrular: {ok} */
export async function telKodDogrula(kod) {
  const r = await storeCall("store-verify-phone-otp", { code: String(kod || "").replace(/\D/g, "") });
  return r.ok ? { ok: true } : { ok: false, mesaj: apiMesaj(r.mesaj, "Kod doğrulanamadı.") };
}

/* ---- Ortak sepet (store-ext; site + panel aynı sepet). Cevap: {ok, items: [{listingID, slug, qty}], count} ---- */
const sepetCevap = (r, varsayilan) =>
  r.ok
    ? { ok: true, items: Array.isArray(r.sonuc?.items) ? r.sonuc.items : [], count: Number(r.sonuc?.count) || 0 }
    : { ok: false, mesaj: apiMesaj(r.mesaj, varsayilan) };

export async function sepetGetir() {
  return sepetCevap(await storeCall("get-store-cart", {}), "Sepet yüklenemedi.");
}

/** Sepet satırı yazar (adet 0 = çıkar). */
export async function sepetYaz(slug, adet) {
  return sepetCevap(await storeCall("set-store-cart-item", { slug, qty: adet }), "Ürün sepete eklenemedi.");
}

/** Yerel (eski) sepeti hesabın sepetine aktarır; sepette olanın adedi değişmez. items: [{slug, qty}] */
export async function sepetBirlestir(items) {
  return sepetCevap(await storeCall("merge-store-cart", { items }), "Sepet aktarılamadı.");
}

/* ---- Sepet siparişi (store-ext; yalnız stage). Website api/store.php siparis-olustur / -ode / -kontrol ile aynı.
   Panel sepeti tarayıcıda tutulur: ürün listesi (slug) gönderilir; fiyat, kargo ve indirim API'de hesaplanır. ---- */

/** Sipariş oluşturur: {ok, siparisID, siparisNo, ara, kargo, indirim, toplam}. */
export async function siparisOlustur(slugs, teslimat, fatura, indirimKodu = "") {
  const r = await storeCall("create-store-order", { items: slugs, delivery: teslimat, billing: fatura, promoCode: indirimKodu });
  if (!r.ok) return { ok: false, mesaj: apiMesaj(r.mesaj, "Sipariş oluşturulamadı.") };
  const x = r.sonuc || {};
  return {
    ok: true,
    siparisID: String(x.orderID || ""),
    siparisNo: String(x.orderNo || ""),
    ara: Number(x.subtotal) || 0,
    kargo: Number(x.shipping) || 0,
    indirim: Number(x.discount) || 0,
    toplam: Number(x.total) || 0,
  };
}

/** Sipariş ödemesini başlatır (PayTR iframe): {ok, iframeUrl, tutar}. */
export async function siparisOde(siparisID) {
  const r = await storeCall("init-order-payment", { orderID: siparisID });
  if (!r.ok) return { ok: false, mesaj: apiMesaj(r.mesaj, "Ödeme başlatılamadı.") };
  const url = String(r.sonuc?.iframeUrl || "");
  if (!/^https:\/\/www\.paytr\.com\//.test(url)) return { ok: false, mesaj: "Ödeme sayfası alınamadı." };
  return { ok: true, iframeUrl: url, tutar: Number(r.sonuc?.amount) || 0 };
}

/** Sipariş ödeme durumu (bekliyorsa PayTR'ye de sorar): {ok, durum}. */
export async function siparisKontrol(siparisID) {
  const r = await storeCall("check-order-payment", { orderID: siparisID });
  return r.ok ? { ok: true, durum: String(r.sonuc?.status || "") } : { ok: false, mesaj: apiMesaj(r.mesaj) };
}

/** Üyenin sepet siparişleri (iptal edilenler hariç — website storeMyOrders ile aynı). */
export async function siparislerim() {
  const r = await storeCall("get-store-orders", {});
  if (!r.ok) return { ok: false, mesaj: apiMesaj(r.mesaj, "Siparişler yüklenemedi.") };
  const liste = (Array.isArray(r.sonuc?.orders) ? r.sonuc.orders : []).filter((o) => String(o.status) !== "iptal");
  return { ok: true, siparisler: liste };
}

/** Sipariş durumu → [etiket, rozet sınıfı] (website storeSiparisDurum). */
export function siparisDurum(st) {
  const m = {
    "odeme-bekliyor": ["Ödeme bekleniyor", "wait"],
    odendi: ["Ödendi · hazırlanıyor", "yes"],
    basarisiz: ["Ödeme başarısız", "sold"],
    iptal: ["İptal edildi", "sold"],
  };
  return m[String(st)] || ["Bilinmiyor", "sold"];
}

/** Hesaba TCKN (users.tckn) — ilan verirken hesapta yoksa bir kez alınır. */
export async function tcknKaydet(tckn) {
  const r = await storeCall("update-user-profile", {
    userID: storeUserID(),
    tckn,
  });
  return r.ok ? { ok: true } : { ok: false, mesaj: apiMesaj(r.mesaj, "Kimlik numarası kaydedilemedi.") };
}

/* ======================================================================= görsel */

/** Görsel dosyasını en fazla 1600 px JPEG'e (0.85) küçültür → data URL ya da null. */
export function fotoKucult(dosya) {
  return new Promise((ok) => {
    if (!dosya || !/^image\//.test(dosya.type)) {
      ok(null);
      return;
    }
    const r = new FileReader();
    r.onerror = () => ok(null);
    r.onload = (e) => {
      const im = new Image();
      im.onload = () => {
        try {
          const o = Math.min(1, 1600 / Math.max(im.width, im.height));
          const c = document.createElement("canvas");
          c.width = Math.round(im.width * o);
          c.height = Math.round(im.height * o);
          const x = c.getContext("2d");
          x.fillStyle = "#fff";
          x.fillRect(0, 0, c.width, c.height);
          x.drawImage(im, 0, 0, c.width, c.height);
          ok(c.toDataURL("image/jpeg", 0.85));
        } catch (err) {
          ok(null);
        }
      };
      im.onerror = () => ok(null);
      im.src = e.target.result;
    };
    r.readAsDataURL(dosya);
  });
}

/** Önizleme kaynağı → API görsel biçimi: hazır URL aynen, data URL → {name, extension, file}. */
export function gorselGovdesi(src) {
  const s = String(src || "");
  if (s.startsWith("data:")) return { name: "ilan.jpg", extension: "jpg", file: s.split(",")[1] || "" };
  return s;
}
